import React, {ChangeEvent, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck} from '@fortawesome/free-solid-svg-icons/faCheck';
import {faExclamationTriangle} from '@fortawesome/free-solid-svg-icons/faExclamationTriangle';
import {faSpinner} from '@fortawesome/free-solid-svg-icons/faSpinner';
import axios from 'axios';

import './DriverMessage.css'
import {PublicApiConsignmentResponse, PublicApiDriverMessageResponse} from "../Representation";

const HTTP_ERROR_MESSAGES = {
    404: "Driver is not currently available.  Please try again later",
    412: "Message cannot be sent for this delivery.  Please refresh the page to get an updated status.",
    429: "You have sent to many messages in a short amount of time.  Please wait a few minutes a try again."
};

interface DriverMessageProps {
    apiOrigin: string
    trackingToken: string
    driverMessages: PublicApiDriverMessageResponse[],
    onMessageSent: (updated: PublicApiConsignmentResponse) => void
}

const DriverMessage = (
    props: DriverMessageProps
) => {

    const [stateSubmitting, setStateSubmitting] = useState(false)
    const [stateMessageSent, setStateMessageSent] = useState(false)
    const [stateMessage, setStateMessage] = useState<string | null>(null)
    const [stateSubmitErrorMessage, setStateSubmitErrorMessage] = useState<string | null>(null)

    const sendMessage = () => {
        if ((stateMessage?.trim().length ?? 0) < 1) {
            setStateSubmitting(false)
            setStateSubmitErrorMessage("Please enter a message")
            return;
        }
        setStateSubmitting(true)
        setStateSubmitErrorMessage(null)
        axios.post<PublicApiConsignmentResponse>(`/tracking/${props.trackingToken}/driver-messages`, {
            message: stateMessage
        }).then((response) => {
            setStateMessageSent(true)
            props.onMessageSent(response.data);
        }).catch(e => {
            const httpStatus: keyof typeof HTTP_ERROR_MESSAGES = e.response?.status ?? 0;
            const submitErrorMessage = HTTP_ERROR_MESSAGES[httpStatus]
                ?? "An unexpected error has occurred sending your message.  Please check your network connection and try again.";
            setStateSubmitErrorMessage(submitErrorMessage);
        }).finally(() => {
            setStateSubmitting(false)
        });
    };

    const sendAnotherMessage = () => {
        setStateSubmitting(false)
        setStateMessageSent(false)
        setStateMessage(null)
        setStateSubmitErrorMessage(null)
    };

    const onMessageChanged = (event: ChangeEvent<any>) => {
        setStateMessage(event.target.value);
    };

    const driverMessages = props.driverMessages.map(msg => {
        return (
            <dl key={msg.id} className="message">
                <dt>Sent:</dt>
                <dd>{msg.formattedSentAt}</dd>
                <dt>Message:</dt>
                <dd>{msg.message}</dd>
            </dl>
        )
    });

    return (
        <div className="driver-message">
            {driverMessages.length > 0 &&
            <div>
                <h3 className="section-header">Previous Messages</h3>
                <div className="previous-messages">
                    {driverMessages}
                </div>
            </div>
            }

            {stateMessageSent &&
            <div>
                <p><FontAwesomeIcon icon={faCheck} /> Your message to the driver has been sent.</p>
                <p><button className="btn btn-submit" type="button" onClick={sendAnotherMessage}>SEND ANOTHER MESSAGE</button></p>
            </div>
            }

            {!stateMessageSent &&
            <div>
                <p className="no-margin">Need to contact the driver? Type your message to the driver here.</p>
                <textarea disabled={stateSubmitting} value={stateMessage ?? undefined} onChange={onMessageChanged} />
                {stateSubmitErrorMessage &&
                    <div className="error">
                        <p><FontAwesomeIcon icon={faExclamationTriangle} /> {stateSubmitErrorMessage}</p>
                    </div>
                }
                <p><button className="btn btn-submit" type="button" onClick={sendMessage}>
                    {stateSubmitting ? <FontAwesomeIcon icon={faSpinner} spin={true} /> : "SEND"}
                </button></p>
            </div>
            }
        </div>
    )

}

export default DriverMessage;
